import { useState, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { knownDevices } from '../data/networkData';
import { mockSecurityData } from './DeviceDetail';

// ── Build deduplicated CVE list across all devices ─────────────

function buildVulnList() {
  const hostnames = Array.from(new Set(Object.values(knownDevices).map(d => d.hostname)));
  const cveMap = {};

  hostnames.forEach(hostname => {
    const d = Object.values(knownDevices).find(x => x.hostname === hostname);
    const mock = mockSecurityData(hostname);
    mock.vulnList.forEach(v => {
      if (!cveMap[v.cve]) {
        cveMap[v.cve] = { ...v, devices: [], os: new Set() };
      }
      if (!cveMap[v.cve].devices.includes(hostname)) {
        cveMap[v.cve].devices.push(hostname);
        if (d?.os) cveMap[v.cve].os.add(d.os);
      }
    });
  });

  return Object.values(cveMap).map(v => ({
    ...v,
    os: Array.from(v.os),
    age: `${2026 - parseInt(v.cve.split('-')[1])} yr${2026 - parseInt(v.cve.split('-')[1]) !== 1 ? 's' : ''}`,
  }));
}

const ALL_VULNS = buildVulnList();
const SEV_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };
ALL_VULNS.sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);

const SEV_COLOR = { Critical: '#f44336', High: '#ff9800', Medium: '#ffeb3b', Low: '#4caf50' };
const ALL_OS = Array.from(new Set(ALL_VULNS.flatMap(v => v.os))).sort();
const ALL_DEVICE_NAMES = Array.from(new Set(ALL_VULNS.flatMap(v => v.devices))).sort();

// ── Stat Box ───────────────────────────────────────────────────

function StatBox({ label, value, color }) {
  return (
    <Paper sx={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px 20px', textAlign: 'center' }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

// ── Filter Chip ────────────────────────────────────────────────

function FilterChip({ label, active, color, onClick }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        backgroundColor: active ? (color ? `${color}33` : '#0d2137') : '#0a1525',
        color: active ? (color || '#00bcd4') : '#7a8fa6',
        border: `1px solid ${active ? (color || '#00bcd4') : '#1e3a5f'}`,
        fontWeight: active ? 700 : 400,
        cursor: 'pointer',
        fontSize: 12,
      }}
    />
  );
}

// ── ColFilter ──────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function Vulnerabilities() {
  // Sidebar filters (single-select, preserved as-is)
  const [filterSev, setFilterSev] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterOS, setFilterOS] = useState([]);

  // Column-level filters (multi-select, independent AND logic)
  const [filterSevCol, setFilterSevCol] = useState([]);
  const [filterStatusCol, setFilterStatusCol] = useState([]);
  const [filterDeviceCol, setFilterDeviceCol] = useState([]);

  const filtered = useMemo(() => ALL_VULNS.filter(v => {
    if (filterSev !== 'All' && v.severity !== filterSev) return false;
    if (filterStatus !== 'All' && v.status !== filterStatus) return false;
    if (filterOS.length > 0 && !v.os.some(o => filterOS.includes(o))) return false;
    if (filterSevCol.length > 0 && !filterSevCol.includes(v.severity)) return false;
    if (filterStatusCol.length > 0 && !filterStatusCol.includes(v.status)) return false;
    if (filterDeviceCol.length > 0 && !v.devices.some(d => filterDeviceCol.includes(d))) return false;
    return true;
  }), [filterSev, filterStatus, filterOS, filterSevCol, filterStatusCol, filterDeviceCol]);

  const counts = useMemo(() => ({
    Critical: ALL_VULNS.filter(v => v.severity === 'Critical').length,
    High:     ALL_VULNS.filter(v => v.severity === 'High').length,
    Medium:   ALL_VULNS.filter(v => v.severity === 'Medium').length,
    Low:      ALL_VULNS.filter(v => v.severity === 'Low').length,
  }), []);

  const toggleOS = (os) => setFilterOS(prev => prev.includes(os) ? prev.filter(o => o !== os) : [...prev, os]);

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatBox label="Critical" value={counts.Critical} color="#f44336" />
        <StatBox label="High"     value={counts.High}     color="#ff9800" />
        <StatBox label="Medium"   value={counts.Medium}   color="#ffeb3b" />
        <StatBox label="Low"      value={counts.Low}      color="#4caf50" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Filter Sidebar */}
        <Paper sx={{ width: 260, flexShrink: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Severity</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, marginBottom: 2.5 }}>
            {['All', 'Critical', 'High', 'Medium', 'Low'].map(s => (
              <FilterChip key={s} label={s} active={filterSev === s} color={SEV_COLOR[s]} onClick={() => setFilterSev(s)} />
            ))}
          </Box>

          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Status</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, marginBottom: 2.5 }}>
            {['All', 'Open', 'Patched'].map(s => (
              <FilterChip key={s} label={s} active={filterStatus === s} color={s === 'Open' ? '#f44336' : s === 'Patched' ? '#4caf50' : undefined} onClick={() => setFilterStatus(s)} />
            ))}
          </Box>

          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Operating System</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {ALL_OS.map(os => (
              <FilterChip key={os} label={os} active={filterOS.includes(os)} onClick={() => toggleOS(os)} />
            ))}
          </Box>
        </Paper>

        {/* Vuln Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Column Headers with ColFilter */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr 90px 140px 80px 60px', padding: '8px 14px', marginBottom: 0.5 }}>
            <ColFilter label="CVE"              options={[]}                              value={[]}              onChange={() => {}} />
            <ColFilter label="Description"      options={[]}                              value={[]}              onChange={() => {}} />
            <ColFilter label="Severity"         options={['Critical', 'High', 'Medium', 'Low']} value={filterSevCol}    onChange={setFilterSevCol} />
            <ColFilter label="Affected Devices" options={ALL_DEVICE_NAMES}                value={filterDeviceCol} onChange={setFilterDeviceCol} />
            <ColFilter label="Status"           options={['Open', 'Patched']}             value={filterStatusCol} onChange={setFilterStatusCol} />
            <ColFilter label="Age"              options={[]}                              value={[]}              onChange={() => {}} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {filtered.length === 0 ? (
              <Typography sx={{ color: '#7a8fa6', padding: 3 }}>No vulnerabilities match the current filters.</Typography>
            ) : filtered.map((v, i) => (
              <Paper key={i} sx={{
                backgroundColor: '#111827',
                border: '1px solid #1e3a5f',
                borderLeft: `3px solid ${SEV_COLOR[v.severity]}`,
                borderRadius: 1,
                display: 'grid',
                gridTemplateColumns: '150px 1fr 90px 140px 80px 60px',
                alignItems: 'center',
                padding: '10px 14px',
                '&:hover': { backgroundColor: '#0d1f33' },
                cursor: 'default',
              }}>
                <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12 }}>{v.cve}</Typography>
                <Typography sx={{ color: '#c0cfe0', fontSize: 13 }}>{v.desc}</Typography>
                <Chip label={v.severity} size="small" sx={{
                  backgroundColor: `${SEV_COLOR[v.severity]}22`,
                  color: SEV_COLOR[v.severity],
                  fontWeight: 700, fontSize: 11, height: 20, width: 'fit-content',
                }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {v.devices.slice(0, 2).map(d => (
                    <Chip key={d} label={d} size="small" sx={{ backgroundColor: '#0a1525', color: '#7a8fa6', fontSize: 10, height: 18 }} />
                  ))}
                  {v.devices.length > 2 && (
                    <Chip label={`+${v.devices.length - 2}`} size="small" sx={{ backgroundColor: '#0a1525', color: '#4a5a70', fontSize: 10, height: 18 }} />
                  )}
                </Box>
                <Chip label={v.status} size="small" sx={{
                  backgroundColor: v.status === 'Open' ? '#2a0a0a' : '#0a2a1a',
                  color: v.status === 'Open' ? '#f44336' : '#4caf50',
                  fontWeight: 700, fontSize: 11, height: 20,
                }} />
                <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>{v.age}</Typography>
              </Paper>
            ))}
          </Box>

          <Typography sx={{ color: '#4a5a70', fontSize: 12, marginTop: 1.5 }}>
            Showing {filtered.length} of {ALL_VULNS.length} findings
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
